import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Lock, ShieldCheck, Timer, Users, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  sport_context: "",
  goal: "",
  timeframe: "",
  motivation: "",
};

const Field = ({
  name,
  label,
  type = "text",
  required = false,
  textarea = false,
  rows = 5,
  placeholder = "",
  value,
  onChange,
  full = false,
}) => (
  <label className={`block group ${full ? "sm:col-span-2" : ""}`}>
    <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 group-focus-within:text-primary-red transition-colors">
      {label}
      {required && <span className="text-primary-red ml-1">*</span>}
    </span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={rows}
        placeholder={placeholder}
        data-testid={`contact-input-${name}`}
        className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-primary-red outline-none py-3 text-white text-base placeholder:text-zinc-700 placeholder:font-light resize-none transition-colors"
      />
    ) : (
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={`contact-input-${name}`}
        className="mt-2 w-full bg-transparent border-b border-white/15 focus:border-primary-red outline-none py-3 text-white text-base placeholder:text-zinc-700 placeholder:font-light transition-colors"
        autoComplete={
          name === "email"
            ? "email"
            : name === "phone"
            ? "tel"
            : name === "first_name"
            ? "given-name"
            : name === "last_name"
            ? "family-name"
            : "off"
        }
      />
    )}
  </label>
);

export const Contact = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!consent) {
      toast.error(t("contact.consent_required"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, { ...form, language: lang });
      if (res?.data?.status === "success") {
        toast.success(t("contact.success"));
        setForm(initialState);
        setConsent(false);
      } else {
        toast.error(t("contact.error"));
      }
    } catch (err) {
      console.error(err);
      toast.error(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  const criteria = t("contact.criteriaItems");

  return (
    <section
      id="kontakt"
      data-testid="section-contact"
      className="relative py-24 lg:py-32 bg-black overflow-hidden"
    >
      <div className="red-glow" style={{ top: "-20%", right: "-20%", opacity: 0.3 }} />
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: intro + criteria + exclusivity */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-primary-red" />
              <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-400">
                {t("contact.eyebrow")}
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.02] tracking-tight">
              {t("contact.title")}
            </h2>
            <p className="mt-6 font-body text-base lg:text-lg text-zinc-400 leading-relaxed">
              {t("contact.subtitle")}
            </p>

            {/* Trust badges */}
            <div className="mt-10 space-y-4">
              <div
                data-testid="capacity-badge"
                className="flex items-center gap-3 border border-primary-red/30 bg-primary-red/5 px-4 py-3"
              >
                <Users size={16} className="text-primary-red" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary-red">
                    {t("contact.capacity")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 border border-white/10 bg-[#0a0a0a] px-4 py-3">
                <Timer size={16} className="text-gold" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                    {t("contact.response")}
                  </div>
                </div>
              </div>
            </div>

            {/* Criteria */}
            <div className="mt-10">
              <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500 mb-4">
                {t("contact.criteria")}
              </div>
              <ul className="space-y-3">
                {criteria.map((c, i) => (
                  <li
                    key={i}
                    data-testid={`criteria-${i}`}
                    className="flex items-start gap-3 group"
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-red shrink-0" />
                    <span className="font-body text-sm text-zinc-300">{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="gold-line my-10" />

            <div className="flex items-start gap-3 max-w-sm">
              <Lock size={16} className="text-gold mt-0.5 shrink-0" />
              <p className="text-xs text-zinc-500 leading-relaxed">
                {t("contact.privacy")}
              </p>
            </div>
          </motion.div>

          {/* Right: application form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={onSubmit}
            className="lg:col-span-7 relative"
            data-testid="contact-form"
          >
            <div className="relative border border-white/10 bg-[#080808] p-8 lg:p-12">
              {/* Corner marks */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t border-l border-primary-red" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t border-r border-primary-red" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-primary-red" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-primary-red" />

              <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="text-primary-red" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-300">
                    {t("contact.eyebrow")} · 2026
                  </span>
                </div>
                <span className="font-mono text-[10px] text-zinc-600 tracking-widest">
                  #LP-{new Date().getFullYear()}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <Field
                  name="first_name"
                  label={t("contact.firstName")}
                  required
                  value={form.first_name}
                  onChange={onChange}
                />
                <Field
                  name="last_name"
                  label={t("contact.lastName")}
                  required
                  value={form.last_name}
                  onChange={onChange}
                />
                <Field
                  name="email"
                  label={t("contact.email")}
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                />
                <Field
                  name="phone"
                  label={t("contact.phone")}
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                />
                <Field
                  name="sport_context"
                  label={t("contact.sport_context")}
                  placeholder={t("contact.sport_placeholder")}
                  value={form.sport_context}
                  onChange={onChange}
                  full
                />
                <Field
                  name="goal"
                  label={t("contact.goal")}
                  placeholder={t("contact.goal_placeholder")}
                  value={form.goal}
                  onChange={onChange}
                  full
                />
                <Field
                  name="timeframe"
                  label={t("contact.timeframe")}
                  placeholder={t("contact.timeframe_placeholder")}
                  value={form.timeframe}
                  onChange={onChange}
                  full
                />
                <Field
                  name="motivation"
                  label={t("contact.motivation")}
                  textarea
                  rows={6}
                  required
                  placeholder={t("contact.motivation_placeholder")}
                  value={form.motivation}
                  onChange={onChange}
                  full
                />
              </div>

              <div className="mt-10 flex flex-col gap-6">
                <label
                  data-testid="consent-label"
                  className="flex items-start gap-3 cursor-pointer group select-none"
                >
                  <input
                    type="checkbox"
                    data-testid="consent-checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="peer sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className="mt-0.5 w-5 h-5 shrink-0 border border-white/25 bg-transparent flex items-center justify-center transition-all duration-200 peer-checked:group-[]:bg-primary-red peer-checked:group-[]:border-primary-red peer-focus-visible:group-[]:ring-2 peer-focus-visible:group-[]:ring-primary-red/40 peer-focus-visible:group-[]:ring-offset-2 peer-focus-visible:group-[]:ring-offset-[#080808]"
                  >
                    <Check
                      size={14}
                      strokeWidth={3}
                      className={`text-white transition-opacity ${
                        consent ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </span>
                  <span className="font-body text-xs sm:text-sm text-zinc-400 leading-relaxed">
                    {t("contact.consent_prefix")}{" "}
                    <Link
                      to="/agb"
                      onClick={(e) => e.stopPropagation()}
                      className="text-white underline underline-offset-4 hover:text-primary-red transition-colors"
                    >
                      {t("contact.consent_terms")}
                    </Link>{" "}
                    {t("contact.consent_and")}{" "}
                    <Link
                      to="/datenschutz"
                      onClick={(e) => e.stopPropagation()}
                      className="text-white underline underline-offset-4 hover:text-primary-red transition-colors"
                    >
                      {t("contact.consent_privacy")}
                    </Link>{" "}
                    {t("contact.consent_suffix")}
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading || !consent}
                  className="btn-primary group w-full sm:w-auto inline-flex items-center justify-center gap-4 px-8 py-5 bg-primary-red hover:bg-[#B3000C] text-white text-sm font-bold uppercase tracking-[0.15em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-primary-red"
                >
                  {loading ? t("contact.sending") : t("contact.submit")}
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
                <p className="text-[11px] text-zinc-500 leading-relaxed max-w-2xl">
                  {t("contact.note")}
                </p>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
